import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, TrendingUp, Award, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function LandingPage() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 grain-texture">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-900 rounded-md flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-blue-900">TopMarks</h1>
          </div>
          <div className="flex space-x-4">
            <Button
              data-testid="login-btn"
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-slate-200"
            >
              Login
            </Button>
            <Button
              data-testid="signup-btn"
              onClick={() => navigate('/register')}
              className="bg-blue-900 hover:bg-blue-800 text-white btn-primary"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-4xl mx-auto space-y-8">
          <div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-4">
              Score Maximum Marks in SSC Board Exams
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Get AI-powered answers following exact SSC exam patterns. Practice with model papers, 
              master important questions, and boost your confidence for 10th board examinations.
            </p>
            <div className="inline-block bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 px-6 py-3 mb-8">
              <p className="text-slate-700 font-medium">
                Built with ❤️ by <span className="text-blue-900 font-bold">R. Gagan Surya Teja</span>
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Also check out{' '}
                <a 
                  href="https://www.bakein.fun" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-900 hover:underline font-medium"
                >
                  BakeIn.fun
                </a>
                {' '}for delicious cakes!
              </p>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4">
            <Button
              data-testid="hero-get-started-btn"
              onClick={() => navigate('/register')}
              className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-6 text-lg btn-primary"
            >
              Start Learning Free
            </Button>
            <Button
              data-testid="hero-learn-more-btn"
              variant="outline"
              className="border-slate-200 px-8 py-6 text-lg"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            >
              Learn More
            </Button>
          </div>
          
          <div className="flex items-center justify-center space-x-8 pt-4">
            <div>
              <p className="text-3xl font-bold text-blue-900">10K+</p>
              <p className="text-sm text-slate-600">Questions Answered</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-900">5K+</p>
              <p className="text-sm text-slate-600">Students Helped</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-900">100%</p>
              <p className="text-sm text-slate-600">Exam Focused</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Everything You Need to Excel
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-slate-50 rounded-md border border-slate-200 card-hover" data-testid="feature-ai-answers">
              <div className="w-12 h-12 bg-blue-900 rounded-md flex items-center justify-center mb-6">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3">AI-Powered Answers</h4>
              <p className="text-slate-600 leading-relaxed">
                Get exam-style answers following SSC correction patterns. Point-wise, keyword-rich, 
                and perfectly formatted for maximum marks.
              </p>
            </div>

            <div className="p-8 bg-slate-50 rounded-md border border-slate-200 card-hover" data-testid="feature-practice-tests">
              <div className="w-12 h-12 bg-blue-900 rounded-md flex items-center justify-center mb-6">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3">Practice Tests</h4>
              <p className="text-slate-600 leading-relaxed">
                Generate unlimited practice tests on any subject. Prepare with questions 
                that mirror actual board exam patterns.
              </p>
            </div>

            <div className="p-8 bg-slate-50 rounded-md border border-slate-200 card-hover" data-testid="feature-track-progress">
              <div className="w-12 h-12 bg-blue-900 rounded-md flex items-center justify-center mb-6">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3">Track Progress</h4>
              <p className="text-slate-600 leading-relaxed">
                Monitor your learning journey with detailed analytics. Save important answers 
                and build your personalized study library.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-blue-900 rounded-lg p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Ace Your Board Exams?
          </h3>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of students who are preparing smarter with TopMarks. 
            Start your journey to scoring maximum marks today.
          </p>
          <Button
            data-testid="cta-start-now-btn"
            onClick={() => navigate('/register')}
            className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-8 py-6 text-lg font-bold btn-primary"
          >
            Start Now - It's Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-600">
          <p>&copy; 2025 TopMarks. Helping students score maximum marks in SSC 10th Board Exams.</p>
        </div>
      </footer>
    </div>
  );
}
